import {
  computed,
  inject,
  onScopeDispose,
  provide,
  readonly,
  ref,
  shallowRef,
  toValue,
  type ComputedRef,
  type InjectionKey,
  type MaybeRefOrGetter,
  type Ref,
} from 'vue';

export interface BvModalActions {
  /**
   * Runs when the modal's confirm button is clicked. When it returns a promise,
   * the confirm button shows a loading state until it settles.
   */
  onConfirm?: () => unknown;
  /** Disables the confirm button (combined with the modal's `disableConfirm` prop). */
  disableConfirm?: MaybeRefOrGetter<boolean>;
  /** Shows the confirm button loading (combined with the modal's `loadingConfirm` prop). */
  loadingConfirm?: MaybeRefOrGetter<boolean>;
}

export interface BvModalContext {
  /** Closes the modal. */
  close: () => void;
  /** Whether the registered `onConfirm` is currently running. */
  isConfirming: Readonly<Ref<boolean>>;
}

interface BvModalProvided extends BvModalContext {
  register: (actions: BvModalActions) => () => void;
}

const BV_MODAL_KEY: InjectionKey<BvModalProvided> = Symbol('bvModal');

/**
 * Provide the modal context to the modal's content. Used by `BvModal` only.
 */
export function provideBvModal(options: { close: () => void }): {
  confirm: () => Promise<void>;
  isConfirming: Readonly<Ref<boolean>>;
  disableConfirm: ComputedRef<boolean>;
  loadingConfirm: ComputedRef<boolean>;
} {
  const actions = shallowRef<BvModalActions | null>(null);
  const isConfirming = ref(false);

  const register = (registered: BvModalActions) => {
    actions.value = registered;
    return () => {
      if (actions.value === registered) {
        actions.value = null;
      }
    };
  };

  const confirm = async () => {
    const onConfirm = actions.value?.onConfirm;
    if (!onConfirm || isConfirming.value) {
      return;
    }
    isConfirming.value = true;
    try {
      await onConfirm();
    } finally {
      isConfirming.value = false;
    }
  };

  provide(BV_MODAL_KEY, {
    close: options.close,
    isConfirming: readonly(isConfirming),
    register,
  });

  return {
    confirm,
    isConfirming: readonly(isConfirming),
    disableConfirm: computed(() => !!toValue(actions.value?.disableConfirm)),
    loadingConfirm: computed(() => isConfirming.value || !!toValue(actions.value?.loadingConfirm)),
  };
}

/**
 * Connect a component rendered inside `BvModal` to the modal's actions.
 *
 * The modal's content is unmounted after the modal closes, so state owned by
 * the calling component starts fresh every time the modal opens. The actions
 * are unregistered automatically when the component unmounts.
 *
 * @example
 * const modal = useBvModal({
 *   onConfirm: async () => {
 *     await api.save(form.value);
 *     modal.close();
 *   },
 *   disableConfirm: () => v$.value.$invalid,
 * });
 */
export function useBvModal(actions: BvModalActions = {}): BvModalContext {
  const modal = inject(BV_MODAL_KEY);
  if (!modal) {
    throw new Error('useBvModal must be used within a BvModal component');
  }
  onScopeDispose(modal.register(actions));
  return {
    close: modal.close,
    isConfirming: modal.isConfirming,
  };
}
